import { LocationData } from './types';
import { CacheManager, WEATHER_CACHE_KEYS } from './utils/cache';
import { RetryManager } from './utils/retry';
import { WeatherConfigManager } from './config';
import { PerformanceMonitor } from './utils/performanceMonitor';
import { WeatherErrorHandler, FallbackLocationData } from './utils/errorHandler';
import { LocationAPIManager } from './apiManager';

export class LocationService {
  static async getCurrentLocation(): Promise<LocationData | FallbackLocationData> {
    PerformanceMonitor.startTimer('location_detection');

    try {
      // Try to get from cache first
      const cached = CacheManager.get<LocationData>(WEATHER_CACHE_KEYS.LOCATION);
      if (cached) {
        PerformanceMonitor.endTimer('location_detection');
        return cached;
      }

      // Try browser geolocation first if available
      const browserLocation = await this.tryBrowserGeolocation();
      if (browserLocation) {
        const config = WeatherConfigManager.getConfig();
        CacheManager.set(WEATHER_CACHE_KEYS.LOCATION, browserLocation, config.locationCacheDuration);
        PerformanceMonitor.endTimer('location_detection');
        return browserLocation;
      }

      // Fall back to IP-based location
      const location = await PerformanceMonitor.measureAPICall(
        'location_api',
        'multiple_endpoints',
        'GET',
        () => RetryManager.withRetry(
          () => LocationAPIManager.fetchLocationData(),
          {
            ...RetryManager.createProgressiveRetryOptions('location'),
            onRetry: (error, attempt) => {
              console.warn(`📍 Location API retry ${attempt}: ${error.message}`);
            }
          }
        )
      );

      // Cache the successful result
      const config = WeatherConfigManager.getConfig();
      CacheManager.set(WEATHER_CACHE_KEYS.LOCATION, location, config.locationCacheDuration);
      PerformanceMonitor.endTimer('location_detection');
      
      if (config.debugMode) {
        console.log('📍 Location detected successfully:', {
          city: location.city,
          country: location.country,
          coordinates: `${location.coordinates.latitude}, ${location.coordinates.longitude}`
        });
      }
      
      return location;
    } catch (error) {
      PerformanceMonitor.endTimer('location_detection');
      return this.handleLocationError(error as Error);
    }
  }

  private static async tryBrowserGeolocation(): Promise<LocationData | null> {
    if (!navigator.geolocation) {
      return null;
    }

    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => resolve(null), 5000); // 5 second timeout

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          clearTimeout(timeoutId);
          try {
            // Reverse geocode to get city/country info
            const locationData = await this.reverseGeocode(
              position.coords.latitude,
              position.coords.longitude
            );
            resolve(locationData);
          } catch (error) {
            console.warn('📍 Reverse geocoding failed:', error);
            resolve(null);
          }
        },
        (error) => {
          clearTimeout(timeoutId);
          console.warn('📍 Browser geolocation failed:', error);
          resolve(null);
        },
        {
          timeout: 5000,
          enableHighAccuracy: false,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }

  private static async reverseGeocode(lat: number, lon: number): Promise<LocationData> {
    // Use a simple reverse geocoding service or fallback to coordinates
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=es`
      );
      
      if (response.ok) {
        const data = await response.json();
        return {
          ip: '0.0.0.0',
          city: data.city || data.locality || 'Ubicación actual',
          region: data.principalSubdivision || '',
          country: data.countryName || 'País desconocido',
          coordinates: { latitude: lat, longitude: lon },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
      }
    } catch (error) {
      console.warn('📍 Reverse geocoding service failed:', error);
    }

    // Fallback to coordinates only
    return {
      ip: '0.0.0.0',
      city: `${lat.toFixed(2)}, ${lon.toFixed(2)}`,
      region: '',
      country: 'Ubicación detectada',
      coordinates: { latitude: lat, longitude: lon },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }

  private static handleLocationError(error: Error): LocationData | FallbackLocationData {
    // Try expired cache first
    const expiredCache = this.getExpiredLocationCache();
    if (expiredCache) {
      console.warn('📍 Using expired location cache due to API failure');
      return expiredCache;
    }

    // Use comprehensive error handling
    const errorResult = WeatherErrorHandler.handle(error, 'location_service', {
      context: 'location_detection',
      enableRetry: false // Already retried above
    });

    console.warn('📍 Using default location fallback:', errorResult.userMessage);
    
    // Cache the fallback location to avoid repeated failures
    const fallbackLocation = errorResult.fallbackData as FallbackLocationData;
    const config = WeatherConfigManager.getConfig();
    CacheManager.set(WEATHER_CACHE_KEYS.LOCATION, fallbackLocation, config.locationCacheDuration);
    
    return fallbackLocation;
  }

  private static getExpiredLocationCache(): LocationData | null {
    try {
      const expiredCache = localStorage.getItem(WEATHER_CACHE_KEYS.LOCATION);
      if (expiredCache) {
        const parsed = JSON.parse(expiredCache);
        // Location cache can be older than weather cache (max 7 days)
        const cacheAge = Date.now() - new Date(parsed.timestamp || 0).getTime();
        if (cacheAge < 7 * 24 * 60 * 60 * 1000) {
          return parsed.data;
        }
      }
    } catch (parseError) {
      console.warn('📍 Failed to parse expired location cache:', parseError);
    }
    return null;
  }



  static clearLocationCache(): void {
    CacheManager.clear(WEATHER_CACHE_KEYS.LOCATION);
  }
}